import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { SquarespaceSection } from "@/components/landing/SquarespaceSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import heroVideoAsset from "@/assets/squarespace-hero.webm.asset.json";
import heroPosterAsset from "@/assets/squarespace-hero-poster.jpg.asset.json";
import floatingVideoAsset from "@/assets/floating-phones.webm.asset.json";
import floatingPosterAsset from "@/assets/floating-phones-poster.jpg.asset.json";


const PUBLISHED = "2026-08-29T00:00:00+00:00";
const MODIFIED = "2026-09-03T00:00:00+00:00";

const TITLE = "Squarespace Websites & Website Modernization | Square Migration";
const DESCRIPTION =
  "Squarespace website design, redesign, and ongoing support, modern, fast, conversion-focused sites plus landing pages for marketing teams and small businesses.";

const FAQS: FaqItem[] = [
  {
    "question": "Do you work on existing Squarespace sites?",
    "answer": "Yes. Redesigns, migrations, one-off page fixes and ongoing support are all welcome alongside new builds."
  },
  {
    "question": "Can our team edit the site ourselves afterward?",
    "answer": "That's the point of Squarespace, your team edits copy and swaps images without a developer, while we handle design, structure and conversion paths."
  },
  {
    "question": "What does a redesign engagement look like?",
    "answer": "Four stages: audit what exists, map the structure, design and build directly in Squarespace, then launch with redirects and analytics handled."
  },
  {
    "question": "Can you migrate us from another CMS?",
    "answer": "Yes. We handle content migration, domain and redirect setup so existing traffic and rankings carry over."
  }
];

export const Route = createFileRoute("/squarespace-websites")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/squarespace-websites" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/squarespace-websites" }],
    scripts: [...serviceSchemaScripts({
      path: "/squarespace-websites",
      name: "Squarespace Websites",
      description: DESCRIPTION,
      serviceType: "Squarespace website design and support",
    }), faqSchemaScript(FAQS), webPageSchemaScript({
      path: "/squarespace-websites",
      name: TITLE,
      description: DESCRIPTION,
      published: PUBLISHED,
      modified: MODIFIED,
    })],
  }),
  component: SquarespaceWebsitesPage,
});

function SquarespaceWebsitesPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Squarespace"
        title="Squarespace websites, refreshed and built to convert."
        description="New builds, redesigns, and ongoing support for Squarespace sites, clean design and clear paths to conversion."
        bullets={["New site builds", "Redesigns & migrations", "Landing pages", "Ongoing support"]}
        backgroundVideo={{
          src: heroVideoAsset.url,
          poster: heroPosterAsset.url,
          ariaLabel: "Squarespace site on a phone, floating in a dark room",
        }}
      />
      <section className="px-5 sm:px-6 md:px-8 lg:px-12 pb-14 md:pb-20 -mt-4">
        <div className="mx-auto max-w-6xl">
          <Link
            to="/squarespace-migrations"
            className="group flex flex-col gap-6 rounded-xl border border-primary/40 bg-primary/5 p-6 transition-colors hover:border-primary/70 hover:bg-primary/10 sm:p-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10"
          >
            <div className="min-w-0 max-w-2xl">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                Moving From Another Platform?
              </p>
              <h2 className="text-2xl font-semibold leading-tight sm:text-3xl">
                We move sites from WordPress, Wix, Webflow and more onto Squarespace without losing your rankings.
              </h2>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                Content inventory, URL mapping with 301 redirects, domain and email setup, then Search Console resubmission so your existing traffic carries over.
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-primary/50 px-6 py-3 text-sm font-semibold text-primary transition-colors group-hover:bg-primary/15">
              See How Migrations Work
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </span>
          </Link>
          <p className="mt-4 text-sm text-muted-foreground">
            Running WordPress or another PHP site? Read{" "}
            <Link to="/php-vulnerability-squarespace" className="text-primary hover:underline">
              what the php2shell PHP vulnerability means for you
            </Link>
            .
          </p>
        </div>
      </section>
      <SquarespaceSection
        eyebrow="Website Services"
        headline={
          <>
            Sites your team can actually <span className="text-primary">keep up to date</span>
          </>
        }
        lead="Squarespace keeps the day-to-day simple: your team edits copy and swaps images without a developer, while we handle design, structure and the pieces that drive conversion."
      />
      <section className="relative overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={floatingVideoAsset.url}
          poster={floatingPosterAsset.url}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#080E1E]/70" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080E1E] via-transparent to-[#080E1E]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-8 py-24 md:py-36 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">
            Made for Mobile

          </p>
          <h2 className="text-3xl md:text-5xl font-bold leading-[1.1] tracking-tight text-white">
            Wherever your customers find you, <span className="text-primary">your site is ready.</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            On the phone in their hand, the laptop at their desk, or the screen in between. Every Squarespace build is designed mobile first and checked screen by screen before launch.
          </p>
        </div>
      </section>
      <ProcessSection

        eyebrow="Website Engagements"
        title="A redesign that doesn’t stall out at month three."
        lead="Clear stages, one round of decisions at a time, and a launch date that holds."
        steps={[
          { number: "01", title: "Audit What Exists", description: "Review current pages, traffic and what’s getting in the way of inquiries." },
          { number: "02", title: "Map the Structure", description: "Decide the pages you need, what each one has to prove, and where visitors convert." },
          { number: "03", title: "Design & Build", description: "Modern layouts, fast pages, real content, assembled directly in Squarespace." },
          { number: "04", title: "Launch & Support", description: "Domain, redirects and analytics handled, with ongoing help whenever you need edits." },
        ]}
      />
      <ClosingCTA
        eyebrow="Website Help"
        headline={
          <>
            Site looking older than <span className="text-primary">your business does?</span>
          </>
        }
        subhead="Let’s fix the first impression."
        body="New build, redesign, CMS migration or just a stubborn page that won’t behave, send the URL and we’ll tell you what’s worth changing first."
        ctaLabel="Review My Website"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/squarespace-websites" />
      <Footer />
    </main>
  );
}
